import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Button, Message, Dropdown } from 'semantic-ui-react';
import { useOrganization, useAuth } from '@clerk/clerk-react';

import Config from '../../constants/Config';

const InviteUserModal = React.memo(
  ({ onClose, onInviteSuccess, onSetSubmitRef, isEmbedded = false }) => {
    const [t] = useTranslation();
    const { organization } = useOrganization();
    const { getToken } = useAuth();
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('org:member');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const roleOptions = [
      { key: 'member', value: 'org:member', text: t('common.organizationMember') },
      { key: 'admin', value: 'org:admin', text: t('common.organizationAdmin') },
    ];

    const handleClose = useCallback(() => {
      setEmail('');
      setRole('org:member');
      setError(null);
      setSuccess(null);
      onClose();
    }, [onClose]);

    const handleSubmit = useCallback(async () => {
      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.trim() || !emailRegex.test(email.trim())) {
        setError(t('common.invalidEmailAddress'));
        return;
      }

      if (!organization) {
        setError(t('common.noOrganizationSelected'));
        return;
      }

      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        // Paso previo: Crear usuario placeholder en backend local para permitir asignación inmediata
        try {
          const token = await getToken();
          const placeholderResponse = await fetch(
            `${Config.SERVER_BASE_URL}/api/users/invite-placeholder`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                email: email.trim(),
                role: role === 'org:admin' ? 'admin' : 'member',
              }),
            }
          );

          if (placeholderResponse.ok) {
            console.log('✅ Placeholder user created locally');
          } else {
            console.warn('Placeholder invitation failed, continuing with Clerk invite...');
          }
        } catch (placeholderError) {
          console.error('Error creating placeholder user:', placeholderError);
          // No bloqueamos el proceso de Clerk si falla el placeholder
        }

        console.log('🔄 Sending invitation via Clerk:', {
          email: email.trim(),
          role,
          organizationId: organization?.id,
        });
        console.log(
          '🔍 Organization object methods:',
          Object.getOwnPropertyNames(organization || {})
        );
        console.log(
          '🔍 Organization prototype methods:',
          organization ? Object.getOwnPropertyNames(Object.getPrototypeOf(organization)) : []
        );

        // Usar solo la API directa de Clerk (sin fallbacks)
        if (!organization) {
          throw new Error('No organization context available. Please contact support.');
        }

        if (!organization.inviteMember) {
          throw new Error(
            'Organization invitations not available in this Clerk version. Please contact support.'
          );
        }

        // Probar diferentes parámetros según la API
        let invitation;
        // Obtener URL actual para redirección (asegura que vuelvan al frontend)
        const redirectUrl = window.location.origin;

        try {
          // Primer intento: con 'email' y redundancia de redirectUrl
          // Nota: Clerk a veces usa camelCase y otras snake_case dependiendo de la versión del SDK/API
          invitation = await organization.inviteMember({
            email: email.trim(),
            role: role,
            redirectUrl,
            redirect_url: redirectUrl,
          });
        } catch (firstError) {
          console.log('🔄 First attempt failed, trying with emailAddress:', firstError.message);
          // Segundo intento: con 'emailAddress' y redundancia
          invitation = await organization.inviteMember({
            emailAddress: email.trim(),
            role: role,
            redirectUrl,
            redirect_url: redirectUrl,
          });
        }

        console.log('✅ Invitation sent successfully:', invitation);

        setSuccess(t('common.invitationSent', { email: email.trim() }));
        setEmail('');

        if (onInviteSuccess) {
          onInviteSuccess(invitation);
        }

        // En modo embebido, limpiar formulario pero no cerrar
        if (!isEmbedded) {
          handleClose();
        }
      } catch (error) {
        console.error('❌ Error sending invitation:', error);

        // Mensajes de error específicos y claros
        let errorMessage = t('common.errorSendingEmail');

        if (error.message) {
          if (error.message.includes('not a function')) {
            errorMessage = t('common.invitationFeatureUnavailable');
          } else if (
            error.message.includes('already invited') ||
            error.message.includes('already exists')
          ) {
            errorMessage = t('common.userAlreadyInvited');
          } else if (error.message.includes('invalid email')) {
            errorMessage = t('common.invalidEmailAddress');
          } else {
            errorMessage = `${t('common.errorSendingEmail')}: ${error.message}`;
          }
        }

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }, [email, role, organization, t, onInviteSuccess, getToken, isEmbedded, handleClose]);

    // Exponer la función de submit para el modal padre
    React.useEffect(() => {
      if (onSetSubmitRef && isEmbedded) {
        onSetSubmitRef(() => handleSubmit);
      }
    }, [onSetSubmitRef, isEmbedded, handleSubmit]);

    const content = (
      <>
        {!isEmbedded && <Modal.Header>{t('common.inviteUserToOrganization')}</Modal.Header>}
        <div style={{ padding: isEmbedded ? 0 : '1rem' }}>
          <Form>
            <Form.Field>
              <label>{t('common.emailAddress')}</label>
              <Form.Input
                type="email"
                placeholder={t('common.enterEmailAddress')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={isLoading}
                error={email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
              />
            </Form.Field>
            <Form.Field>
              <label>{t('common.role')}</label>
              <Dropdown
                selection
                fluid
                value={role}
                options={roleOptions}
                onChange={(e, { value }) => setRole(value)}
                disabled={isLoading}
              />
            </Form.Field>
            {error && (
              <Message negative>
                <Message.Header>{t('common.error')}</Message.Header>
                <p>{error}</p>
              </Message>
            )}
            {success && (
              <Message positive>
                <Message.Header>{t('common.success')}</Message.Header>
                <p>{success}</p>
              </Message>
            )}
          </Form>
        </div>
        {!isEmbedded && (
          <Modal.Actions>
            <Button onClick={handleClose} disabled={isLoading}>
              {t('action.cancel')}
            </Button>
            <Button
              primary
              loading={isLoading}
              disabled={!email.trim() || isLoading}
              onClick={handleSubmit}
            >
              {t('action.sendInvitation')}
            </Button>
          </Modal.Actions>
        )}
      </>
    );

    return isEmbedded ? (
      content
    ) : (
      <Modal open onClose={handleClose} size="small">
        {content}
      </Modal>
    );
  }
);

InviteUserModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onInviteSuccess: PropTypes.func,
  onSetSubmitRef: PropTypes.func,
  isEmbedded: PropTypes.bool,
};

InviteUserModal.defaultProps = {
  onInviteSuccess: undefined,
};

export default InviteUserModal;
